<template>
  <div class="ai-context-groups-table" data-testid="ai-context-groups-table">
    <div v-show="loading" class="ai-context-groups-table__shimmer-wrap">
      <lb-datatablenew
        data-testid="ai-context-groups-table__shimmer"
        :headers="headers"
        :items="shimmerItems"
        :loading="false"
        :enableslot="shimmerSlots"
        :perpage="itemsPerPage"
        :hide-pagination="true"
        class="elevation-0 ai-context-groups-table__datatable"
      >
        <template v-for="slot in shimmerSlots" v-slot:[slot]>
          <div :key="slot" class="ai-context-groups-table__shimmer-cell">
            <v-skeleton-loader
              v-if="slot === 'gl_codes' || slot === 'networks'"
              type="chip"
              class="ai-context-groups-table__shimmer-chip"
            />
            <v-skeleton-loader
              v-else-if="slot === 'actions'"
              type="avatar"
              class="ai-context-groups-table__shimmer-action"
            />
            <v-skeleton-loader
              v-else
              type="text"
              class="ai-context-groups-table__shimmer-text"
            />
          </div>
        </template>
      </lb-datatablenew>
    </div>

    <div v-show="!loading" class="ai-context-groups-table__data-wrap">
      <lb-datatablenew
        :headers="headers"
        :items="displayItems"
        :loading="false"
        :enableslot="['gl_codes', 'networks', 'context', 'actions']"
        :perpage="itemsPerPage"
        :hide-pagination="true"
        :bypassFiltering="true"
        class="elevation-0 ai-context-groups-table__datatable"
        data-testid="ai-context-groups-table__datatable"
        @update:filters="onFiltersUpdate"
      >
        <template v-slot:item.gl_codes="{ item }">
          <AiContextChipList
            :chips="item.gl_codes || []"
            type="gl"
            :limit="2"
            test-id="ai-context-groups-table__gl-chips"
          />
        </template>
        <template v-slot:gl_codes="{ item }">
          <AiContextChipList
            :chips="item.gl_codes || []"
            type="gl"
            :limit="2"
            test-id="ai-context-groups-table__gl-chips"
          />
        </template>
        <template v-slot:item.networks="{ item }">
          <AiContextChipList
            :chips="item.networks || []"
            type="network"
            :limit="2"
            test-id="ai-context-groups-table__network-chips"
          />
        </template>
        <template v-slot:networks="{ item }">
          <AiContextChipList
            :chips="item.networks || []"
            type="network"
            :limit="2"
            test-id="ai-context-groups-table__network-chips"
          />
        </template>
        <template v-slot:item.context="{ item }">
          <div
            class="ai-context-groups-table__context"
            :data-testid="`ai-context-groups-table__context-${item._id}`"
          >
            <template v-if="contextText(item)">
              <span
                class="ai-context-groups-table__context-text"
                :class="{ 'line-clamp-2': !isContextExpanded(item._id) && isContextLong(item) }"
              >
                {{ contextDisplayText(item) }}
              </span>
              <button
                v-if="isContextLong(item)"
                type="button"
                class="ai-context-groups-table__context-toggle"
                :data-testid="`ai-context-groups-table__context-toggle-${item._id}`"
                @click.stop="toggleContext(item._id)"
              >
                {{ isContextExpanded(item._id) ? 'Show less' : 'Show more' }}
              </button>
            </template>
            <span v-else class="ai-context-groups-table__context-empty">—</span>
          </div>
        </template>
        <template v-slot:context="{ item }">
          <div
            class="ai-context-groups-table__context"
            :data-testid="`ai-context-groups-table__context-${item._id}`"
          >
            <template v-if="contextText(item)">
              <span
                class="ai-context-groups-table__context-text"
                :class="{ 'line-clamp-2': !isContextExpanded(item._id) && isContextLong(item) }"
              >
                {{ contextDisplayText(item) }}
              </span>
              <button
                v-if="isContextLong(item)"
                type="button"
                class="ai-context-groups-table__context-toggle"
                :data-testid="`ai-context-groups-table__context-toggle-${item._id}`"
                @click.stop="toggleContext(item._id)"
              >
                {{ isContextExpanded(item._id) ? 'Show less' : 'Show more' }}
              </button>
            </template>
            <span v-else class="ai-context-groups-table__context-empty">—</span>
          </div>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn
            icon
            small
            data-testid="ai-context-groups-table__edit-btn"
            @click="$emit('edit', item)"
          >
            <img src="/img/icons/PenEditIcon.svg" alt="Edit">
          </v-btn>
        </template>
        <template v-slot:actions="{ item }">
          <v-btn
            icon
            small
            data-testid="ai-context-groups-table__edit-btn"
            @click="$emit('edit', item)"
          >
            <img src="/img/icons/PenEditIcon.svg" alt="Edit">
          </v-btn>
        </template>
        <template v-slot:no-data>
          <div class="ai-context-groups-table__empty text-center pa-6" data-testid="ai-context-groups-table__empty">
            No context groups configured yet.
          </div>
        </template>
      </lb-datatablenew>
    </div>
  </div>
</template>

<script>
import AiContextChipList from './AiContextChipList.vue';
import {
  collectGlFilterOptions,
  collectGroupNameFilterOptions,
  collectNetworkFilterOptions,
  formatGroupNameLabel,
} from '../../../utils/aiContextHelpers';

const CONTEXT_PREVIEW_CHARS = 1000;
const SHIMMER_ROW_COUNT = 8;

export default {
  name: 'AiContextGroupsTable',
  components: { AiContextChipList },
  props: {
    items: { type: Array, default: () => [] },
    /** Full dataset used to build column filter option lists (not just current page). */
    filterSourceItems: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    page: { type: Number, default: 0 },
    itemsPerPage: { type: Number, default: 10 },
  },
  data() {
    return {
      expandedContextIds: {},
      shimmerSlots: ['sno', 'group_name', 'gl_codes', 'networks', 'context', 'actions'],
    };
  },
  computed: {
    groupNameFilterList() {
      return collectGroupNameFilterOptions(this.filterSourceItems);
    },
    glFilterList() {
      return collectGlFilterOptions(this.filterSourceItems);
    },
    networkFilterList() {
      return collectNetworkFilterOptions(this.filterSourceItems);
    },
    headers() {
      return [
        { text: 'S.no', value: 'sno', sortable: false, width: '70px', cellClass: 'ai-context-groups-table__cell' },
        {
          text: 'Category Name',
          value: 'group_name',
          sortable: false,
          filterable: true,
          filterList: this.groupNameFilterList,
          cellClass: 'ai-context-groups-table__cell',
        },
        {
          text: 'GL Code & Description',
          value: 'gl_codes',
          sortable: false,
          filterable: true,
          filterList: this.glFilterList,
          cellClass: 'ai-context-groups-table__cell',
        },
        {
          text: 'Network',
          value: 'networks',
          sortable: false,
          filterable: true,
          filterList: this.networkFilterList,
          cellClass: 'ai-context-groups-table__cell',
        },
        { text: 'Context', value: 'context', sortable: false, cellClass: 'ai-context-groups-table__cell' },
        { text: 'Actions', value: 'actions', sortable: false, width: '100px', cellClass: 'ai-context-groups-table__cell' },
      ];
    },
    shimmerItems() {
      return Array.from({ length: SHIMMER_ROW_COUNT }, (_, index) => ({
        _id: `shimmer-${index}`,
        sno: '',
        group_name: '',
        gl_codes: [],
        networks: [],
        context: '',
        actions: '',
      }));
    },
    displayItems() {
      const start = this.page * this.itemsPerPage;
      return (this.items || []).map((item, idx) => ({
        ...item,
        sno: `${start + idx + 1}.`,
        group_name: formatGroupNameLabel(item) || '—',
      }));
    },
  },
  watch: {
    items() {
      this.expandedContextIds = {};
    },
  },
  methods: {
    onFiltersUpdate(filters) {
      this.$emit('update:filters', filters || {});
    },
    contextText(item) {
      const raw = item?.context;
      if (raw == null) return '';
      return String(raw).trim();
    },
    contextDisplayText(item) {
      const text = this.contextText(item);
      if (!text) return '';
      if (this.isContextExpanded(item._id) || !this.isContextLong(item)) return text;
      return `${text.slice(0, CONTEXT_PREVIEW_CHARS)}…`;
    },
    isContextLong(item) {
      return this.contextText(item).length > CONTEXT_PREVIEW_CHARS;
    },
    isContextExpanded(id) {
      return !!this.expandedContextIds[id];
    },
    toggleContext(id) {
      this.$set(this.expandedContextIds, id, !this.expandedContextIds[id]);
    },
  },
};
</script>

<style lang="scss" scoped>
@import './AiContextGroupsTable.scss';
</style>
